import React, { useState, useEffect, useMemo } from 'react';
import { useForm, useWatch, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useToast } from 'use-toast-mui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, TextField, InputAdornment, IconButton, FormControlLabel } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QueryString from 'qs';

import CommonModalLayout from '../../../form-components/CommonModalLayout';
import CommonModalButton from '../../../form-components/CommonModalButton';
import { FormInputTextArea } from '../../../form-components/FormInputTextArea';
import CustomRadioBox from '../../CustomRadioBox';
import CardPaymentForm from '../../manualSales/payment/cardPaymentForm';
import CashPaymentForm from '../../manualSales/payment/cashPaymentForm';
import BankTransferForm from '../../manualSales/payment/bankPaymentForm';
import SellerShareSelect, { buildSellerPayload, isSellerShareValid } from '../../common/SellerShareSelect';

import { getMemberByBranchAndTemporary } from '../../../api/member-management';
import { getBranchManager } from '../../../api/staff';
import { fetchTemporaryUserDetail, searchUserByPhoneNumber } from '../../../api/temporaryUser';
import { directTransferTicket, directTransferPass, directTransferProduct, directTransferLocker } from '../../../api/transfer';
import { errorMessages, formatterCurrency } from '../../../utils/methods';
import { TRANSFERABLE_PRODUCTS_KEY } from './hooks/useTransferableProducts';
import {
  INITIAL_LOCKER_TRANSFER,
  RECEIVER_SPOT,
  LockerPassPicker,
  LockerReceiverSpotPicker,
  useLockerBundle,
} from './LockerTransferSection';


import {
  ModalSectionLabel,
  RadioRow,
  TwoColRow,
  ColGroup,
  FieldGroupLabel,
  PaidSection,
  AmountInputWrapper,
  AmountUnit,
  ToggleGroup,
  ToggleButton,
  PreviewList,
  PreviewItem,
  PreviewName,
  PreviewTag,
  PreviewPhone,
  PreviewEmpty,
  FooterCenter,
  TransferSection,
  TransferSectionLabel,
  TransferHelperText,
  TransferInfoCard,
  TransferInfoName,
} from './UserTransferStyled';

const FEE_PAID = 'PAID';
const FEE_UNPAID = 'UNPAID';

// 환불수단(결제수단) 토글 — paymentMethod에 매핑
const PAY_METHODS = ['카드', '현금', '계좌이체'];

// 숫자 입력 → 콤마 제거 후 정수
const toNumber = value => Number(String(value ?? '').replace(/[^\d]/g, '')) || 0;

const GENDER_LABEL = { MALE: '남', FEMALE: '여' };
const dateOnly = value => (value ? String(value).slice(0, 10) : '-');

const DIRECT_TRANSFER_API = {
  TICKET: directTransferTicket,
  PASS: directTransferPass,
  PRODUCT: directTransferProduct,
  LOCKER: directTransferLocker,
};

const TransferModal = ({ open, onClose, product, user, bigUser }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const token = bigUser?.token;
  const headBranchId = bigUser?.headBranch?.headBranchId;
  const branchId = bigUser?.branch?.branchId;

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedReceiver, setSelectedReceiver] = useState(null);
  // 잠재회원 양수자와 번호가 같은 기존 회원. 안내만 하고 고르지 않는다(2026-10-22) — 양도는 새 회원으로.
  const [linkCandidates, setLinkCandidates] = useState([]);
  // 이미 앱 계정이 연동된 잠재회원이면 후보 선택 없이 그 계정으로 양도된다(서버가 USER_ID 로 처리).
  const [receiverLinkedUsername, setReceiverLinkedUsername] = useState(null);
  const [linkLookup, setLinkLookup] = useState('idle'); // idle | loading | ready | error
  const [linkLookupRetry, setLinkLookupRetry] = useState(0);
  const [branchManagers, setBranchManagers] = useState([]);

  const { control, handleSubmit, setValue, reset } = useForm({
    defaultValues: {
      managerId: [], // #122 양도 수수료 주문 판매담당자 N명(user ID 배열)
      sellerShares: {},
      feePaid: FEE_UNPAID,
      feeAmount: '',
      outstandingAmount: '',
      paymentMethod: null,
      memo: '',
      // 결제수단 폼(card/cash/bank) 공용 필드
      installmentMonth: '00',
      cardCompanyCode: null,
      cardNumber: '',
      approvalNumber: '',
      receiptYn: 'Y',
      deductionType: 0,
      bankCode: null,
      bankInfo: '',
      depositorName: '',
    },
  });

  const feePaid = useWatch({ control, name: 'feePaid' });
  const paymentMethod = useWatch({ control, name: 'paymentMethod' });
  const isFeePaid = feePaid === FEE_PAID;
  // 기간 상품·락커는 정회원에게만 양도한다(잠재회원 불가). 기간 상품은 유효 수강권·이용권 보유자만, 락커는 만료·중지 회원도 가능. 서버도 같은 기준.
  const isLocker = product?.productType === 'LOCKER';
  const memberOnly = product?.productType === 'PRODUCT' || isLocker;
  // 락커(#166) — 넘길 이용권 묶음 + 양수자 자리(자리 양도 / 다른 자리 배정 / 미배정)
  const [lockerTransfer, setLockerTransfer] = useState(INITIAL_LOCKER_TRANSFER);
  const visibleResults = memberOnly ? searchResults.filter(m => m.userType !== 'T') : searchResults;

  // 모달 열릴 때 담당자 목록 조회 + 폼 초기화
  useEffect(() => {
    if (!open) return;
    reset();
    setSearchInput('');
    setSearchResults([]);
    setSelectedReceiver(null);
    setLinkCandidates([]);
    setReceiverLinkedUsername(null);
    setLinkLookup('idle');
    setLockerTransfer(INITIAL_LOCKER_TRANSFER);

    if (branchId && token) {
      getBranchManager(branchId, token)
        .then(data => setBranchManagers((data || []).map(m => ({ value: m.managerId, label: m.managerName }))))
        .catch(err => toast.error(errorMessages(err)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, branchId, token]);

  // 위 초기화 effect 뒤에 둔다 — 같은 커밋에서 effect 는 선언 순으로 돌아, 앞에 두면 이용권이 캐시돼 있을 때
  // 훅이 넣은 기본 선택(고른 이용권)을 위 초기화가 덮어써 passIds 가 비고 '자리 양도'를 고를 수 없게 된다.
  const lockerBundle = useLockerBundle({
    open,
    product,
    senderMemberId: user?.memberId,
    token,
    branchId,
    value: lockerTransfer,
    onChange: setLockerTransfer,
  });

  // 회원검색(잠재+일반). 2글자 이상부터 조회.
  const handleSearch = async () => {
    if (!searchInput || searchInput.trim().length < 2) {
      toast.error(t('두 글자 이상 입력해주세요.'));
      return;
    }
    setSearchLoading(true);
    try {
      const query = QueryString.stringify({ searchInput: searchInput.trim(), headBranchId, branchId });
      const data = await getMemberByBranchAndTemporary(token, query);
      setSearchResults(data?.content ?? []);
    } catch (err) {
      toast.error(errorMessages(err));
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // 잠재회원을 양수자로 고르면 앱 계정 연동 여부를 확인하고, 없으면 같은 번호의 기존 회원 후보를 조회한다.
  // 검색 응답의 잠재회원 memberId 가 temporary_user ID 다(userId 는 다른 값).
  const receiverIsTemporary = selectedReceiver?.userType === 'T';
  const receiverTemporaryId = selectedReceiver?.memberId;
  const receiverPhone = selectedReceiver?.phoneNumber;
  useEffect(() => {
    setLinkCandidates([]);
    setReceiverLinkedUsername(null);
    if (!receiverIsTemporary || !branchId) {
      setLinkLookup('idle');
      return undefined;
    }
    let canceled = false;
    setLinkLookup('loading');
    (async () => {
      try {
        const detail = await fetchTemporaryUserDetail(receiverTemporaryId);
        if (canceled) return;
        if (detail?.linkedUserId) {
          setReceiverLinkedUsername(detail.linkedUsername || '-');
          setLinkLookup('ready');
          return;
        }
        const results = receiverPhone
          ? await searchUserByPhoneNumber(String(receiverPhone).replaceAll('-', ''), branchId, true)
          : [];
        if (canceled) return;
        // 양도하는 회원 본인은 후보에서 뺀다(같은 번호여도 자기 자신에게는 양도할 수 없다).
        setLinkCandidates((results ?? []).filter(candidate => candidate.memberId !== user?.memberId));
        setLinkLookup('ready');
      } catch (err) {
        if (canceled) return;
        toast.error(errorMessages(err));
        setLinkLookup('error');
      }
    })();
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiverIsTemporary, receiverTemporaryId, receiverPhone, branchId, linkLookupRetry]);

  const mutation = useMutation({
    mutationFn: ({ productId, productType, body }) => DIRECT_TRANSFER_API[productType](productId, body, token),
    onSuccess: () => {
      toast.success(t('양도가 완료되었습니다.'));
      queryClient.invalidateQueries({ queryKey: [TRANSFERABLE_PRODUCTS_KEY] });
      // 락커 묶음 후보·회원상세 이용권 목록이 같은 키를 쓴다 — 넘긴 이용권이 사용 중으로 남지 않게
      if (product?.productType === 'LOCKER') queryClient.invalidateQueries({ queryKey: ['generalProducts'] });
      onClose();
    },
    onError: err => toast.error(errorMessages(err)),
  });

  const submit = values => {
    if (!selectedReceiver) {
      toast.error(t('양도받을 회원을 선택해주세요.'));
      return;
    }
    // 담당자(판매담당자·비율)는 수수료 매출(member_order)이 생길 때만 의미가 있다 — 미결제면 주문이 없어 서버가 쓰지 않는다
    if (isFeePaid && !values.managerId?.length) {
      toast.error(t('담당자를 선택해주세요.'));
      return;
    }
    if (isFeePaid && !isSellerShareValid(values.managerId, values.sellerShares)) {
      toast.error(t('판매 담당자 귀속 비율의 합은 100% 여야 합니다'));
      return;
    }
    if (receiverIsTemporary && linkLookup !== 'ready') {
      toast.error(t(linkLookup === 'error' ? '양수자 확인에 실패했습니다. 다시 시도해주세요.' : '양수자를 확인하는 중입니다.'));
      return;
    }
    if (isLocker && lockerTransfer.receiverSpot === RECEIVER_SPOT.OTHER && !lockerTransfer.target) {
      toast.error(t('배정할 라커 자리를 선택하세요.'));
      return;
    }
    const seller = isFeePaid ? buildSellerPayload(values.managerId, values.sellerShares) : { registeredBy: null };

    // userType 'M'=일반회원 → receiverMemberId, 'T'=잠재회원 → receiverTemporaryUserId(숫자)
    const isTemporary = selectedReceiver.userType === 'T';

    const body = {
      receiverMemberId: isTemporary ? null : selectedReceiver.memberId,
      receiverTemporaryUserId: isTemporary ? Number(selectedReceiver.memberId) : null,
      managerId: seller.registeredBy, // 첫 담당자(구 서버 호환), 미결제면 null
      registeredByIds: seller.registeredByIds || null, // #122
      shareRates: seller.shareRates || null,
      feePaid: isFeePaid,
      memo: values.memo || null,
      ...(isLocker && {
        memberLockerPassIds: lockerTransfer.passIds,
        receiverSpot: lockerTransfer.receiverSpot,
        targetLockerId: lockerTransfer.target?.lockerId ?? null,
        lockerPlanEntries: lockerTransfer.planEntries,
      }),
    };

    // 수수료 결제 시에만 금액/결제수단 정보 포함 (미결제=면제)
    if (isFeePaid) {
      Object.assign(body, {
        feeAmount: toNumber(values.feeAmount),
        outstandingAmount: toNumber(values.outstandingAmount),
        paymentMethod: values.paymentMethod || null,
        cardCompanyCode: values.paymentMethod === '카드' ? values.cardCompanyCode || null : null,
        cardNumber: values.paymentMethod === '카드' ? values.cardNumber || null : null,
        installmentMonth: values.paymentMethod === '카드' ? values.installmentMonth || null : null,
        cId: values.approvalNumber || null,
        bankInfo: values.paymentMethod === '계좌이체' ? values.bankInfo || null : null,
        depositorName: values.paymentMethod === '계좌이체' ? values.depositorName || null : null,
      });
    }

    mutation.mutate({ productId: product.productId, productType: product.productType, body });
  };

  const footer = useMemo(
    () => (
      <FooterCenter>
        <CommonModalButton buttonVariant="cancel" onClick={onClose}>
          {t('취소')}
        </CommonModalButton>
        <CommonModalButton buttonVariant="submit" disabled={mutation.isPending} onClick={handleSubmit(submit)}>
          {t('양도 완료')}
        </CommonModalButton>
      </FooterCenter>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mutation.isPending, selectedReceiver, isFeePaid, paymentMethod, lockerTransfer, linkCandidates, linkLookup],
  );

  return (
    <CommonModalLayout
      open={open}
      onClose={onClose}
      title={t('양도')}
      description=""
      width={680}
      footer={footer}
      disableEscapeKeyDown>
      {/* 1. 양도할 이용권 */}
      <TransferSection>
        <TransferSectionLabel>{t('양도할 이용권')}</TransferSectionLabel>
        <LockerPassPicker
          bundle={lockerBundle}
          product={product}
          renderSingle={() => (
            <TransferInfoCard>
              <TransferInfoName>{product?.productName}</TransferInfoName>
              {product?.lockerSpot && <span>{product.lockerSpot}</span>}
              {product?.startDate && (
                <span>
                  {product.startDate} ~ {product.expireDate}
                </span>
              )}
              {product?.remainingCount != null && (
                <span>
                  {t('잔여')} {product.remainingCount}
                  {t('회')}
                </span>
              )}
            </TransferInfoCard>
          )}
        />
      </TransferSection>

      {/* 2. 양수자 — 검색해 고르면 목록을 접고 선택한 회원만 보인다 */}
      <TransferSection>
        <TransferSectionLabel>{t('양수자')}</TransferSectionLabel>
        {selectedReceiver ? (
          <TransferInfoCard>
            <TransferInfoName>
              {selectedReceiver.name}
              {selectedReceiver.userType === 'T' ? ` (${t('잠재')})` : ''}
            </TransferInfoName>
            <span>{selectedReceiver.phoneNumber}</span>
            <span>{selectedReceiver.branchName}</span>
            <span>{t(GENDER_LABEL[selectedReceiver.gender] ?? selectedReceiver.gender ?? '-')}</span>
            <span>{dateOnly(selectedReceiver.birthDate)}</span>
            <Button size="small" onClick={() => setSelectedReceiver(null)} sx={{ ml: 'auto', minWidth: 0 }}>
              {t('변경')}
            </Button>
          </TransferInfoCard>
        ) : (
          <>
            <TextField
              fullWidth
              size="small"
              placeholder={t('회원명 또는 전화번호 검색')}
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: '8px', height: '44px' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch} disabled={searchLoading} edge="end">
                      <SearchIcon sx={{ color: '#a6a6a6' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {(searchLoading || searchResults.length > 0) && (
              <PreviewList sx={{ marginBottom: 0 }}>
                {visibleResults.length === 0 ? (
                  <PreviewEmpty>{searchLoading ? t('검색 중...') : t('검색 결과가 없습니다.')}</PreviewEmpty>
                ) : (
                  visibleResults.map(m => (
                    <PreviewItem key={`${m.userType}-${m.memberId}`} onClick={() => setSelectedReceiver(m)}>
                      <PreviewName>{m.name}</PreviewName>
                      {m.userType === 'T' && <PreviewTag>({t('잠재')})</PreviewTag>}
                      <PreviewPhone>{m.phoneNumber}</PreviewPhone>
                    </PreviewItem>
                  ))
                )}
              </PreviewList>
            )}
            {memberOnly && (
              <TransferHelperText>
                {isLocker
                  ? t('라커는 이 지점 회원에게만 양도할 수 있습니다(수강권이 만료·중지여도 가능, 잠재회원 불가).')
                  : t('기간 상품은 이 지점에 이용 중이거나 중지된 수강권·이용권이 있는 정회원에게만 양도할 수 있습니다.')}
              </TransferHelperText>
            )}
          </>
        )}
      </TransferSection>

      {/* 2-1. 잠재회원 양수자와 번호가 같은 기존 회원 — 안내만(앱 연동된 잠재회원은 그 계정으로, 아니면 새 회원으로 양도) */}
      {receiverIsTemporary && linkLookup === 'error' && (
        <TransferSection>
          <TransferHelperText sx={{ marginTop: 0 }}>
            {t('양수자의 앱 연동·같은 번호 회원을 확인하지 못했습니다.')}
            <Button size="small" onClick={() => setLinkLookupRetry(n => n + 1)}>
              {t('다시 시도')}
            </Button>
          </TransferHelperText>
        </TransferSection>
      )}
      {receiverIsTemporary && receiverLinkedUsername && (
        <TransferSection>
          <TransferHelperText sx={{ marginTop: 0 }}>
            {t('앱 연동 계정({{username}})으로 양도됩니다.', { username: receiverLinkedUsername })}
          </TransferHelperText>
        </TransferSection>
      )}
      {receiverIsTemporary && linkCandidates.length > 0 && (
        <TransferSection>
          <TransferSectionLabel>{t('같은 번호의 기존 회원')}</TransferSectionLabel>
          <TransferHelperText sx={{ marginBottom: '8px', marginTop: 0 }}>
            {t('같은 번호로 등록된 회원이 있습니다. 양도하면 이 번호로 새 회원이 등록됩니다.')}
          </TransferHelperText>
          <PreviewList sx={{ marginBottom: 0 }}>
            {linkCandidates.map(candidate => (
              <PreviewItem key={candidate.userId} sx={{ cursor: 'default', '&:hover': { background: '#fff' } }}>
                <PreviewName>{candidate.name}</PreviewName>
                <PreviewPhone>{[candidate.phoneNumber, candidate.birthDate].filter(Boolean).join(' · ')}</PreviewPhone>
              </PreviewItem>
            ))}
          </PreviewList>
        </TransferSection>
      )}

      {/* 3. 라커 — 양수자 자리 */}
      {isLocker && (
        <TransferSection>
          <TransferSectionLabel>{t('양수자 라커 자리')}</TransferSectionLabel>
          <LockerReceiverSpotPicker
            bundle={lockerBundle}
            product={product}
            receiver={selectedReceiver}
            token={token}
            branchId={branchId}
            open={open}
          />
        </TransferSection>
      )}

      {/* 4. 수수료 결제 여부(좌) + 담당자(우) — 한 줄 2단 */}
      <TwoColRow sx={{ marginBottom: '24px' }}>
        <ColGroup>
          <FieldGroupLabel>{t('수수료 결제 여부')}</FieldGroupLabel>
          <RadioRow sx={{ height: '44px' }}>
            <FormControlLabel
              control={<CustomRadioBox checked={feePaid === FEE_UNPAID} onChange={() => setValue('feePaid', FEE_UNPAID)} />}
              label={t('미결제')}
            />
            <FormControlLabel
              control={<CustomRadioBox checked={isFeePaid} onChange={() => setValue('feePaid', FEE_PAID)} />}
              label={t('결제')}
            />
          </RadioRow>
        </ColGroup>
        {/* #122 담당자·귀속 비율은 수수료 매출이 생기는 '결제'일 때만 — 미결제면 주문이 없어 쓸 데가 없다 */}
        {isFeePaid && (
          <ColGroup>
            <FieldGroupLabel>{t('판매 담당자')}</FieldGroupLabel>
            <SellerShareSelect
              control={control}
              setValue={setValue}
              name="managerId"
              sharesName="sellerShares"
              options={branchManagers}
              placeholder={t('담당자를 선택해 주세요.')}
            />
          </ColGroup>
        )}
      </TwoColRow>

      {/* 결제 선택 시에만: 양도수수료/미수금/환불수단/결제정보 */}
      {isFeePaid && (
        <PaidSection>
          {/* 양도 수수료(좌) + 미수금(우) */}
          <TwoColRow sx={{ marginBottom: '16px' }}>
            <ColGroup>
              <FieldGroupLabel>{t('양도 수수료')}</FieldGroupLabel>
              <Controller
                control={control}
                name="feeAmount"
                render={({ field }) => (
                  <AmountInputWrapper>
                    <input
                      style={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#141414',
                        background: 'transparent',
                      }}
                      placeholder={t('0')}
                      value={field.value}
                      onChange={e => field.onChange(formatterCurrency(toNumber(e.target.value)))}
                    />
                    <AmountUnit>{t('원')}</AmountUnit>
                  </AmountInputWrapper>
                )}
              />
            </ColGroup>
            <ColGroup>
              <FieldGroupLabel>{t('미수금')}</FieldGroupLabel>
              <Controller
                control={control}
                name="outstandingAmount"
                render={({ field }) => (
                  <AmountInputWrapper>
                    <input
                      style={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px',
                        fontWeight: 500,
                        color: '#141414',
                        background: 'transparent',
                      }}
                      placeholder={t('0')}
                      value={field.value}
                      onChange={e => field.onChange(formatterCurrency(toNumber(e.target.value)))}
                    />
                    <AmountUnit>{t('원')}</AmountUnit>
                  </AmountInputWrapper>
                )}
              />
            </ColGroup>
          </TwoColRow>

          {/* 결제수단 — 카드/현금/계좌이체 토글 버튼 */}
          <ColGroup style={{ marginBottom: '16px' }}>
            <FieldGroupLabel>{t('결제수단')}</FieldGroupLabel>
            <ToggleGroup>
              {PAY_METHODS.map(method => (
                <ToggleButton
                  key={method}
                  active={paymentMethod === method}
                  onClick={() => setValue('paymentMethod', method)}>
                  {t(method)}
                </ToggleButton>
              ))}
            </ToggleGroup>
          </ColGroup>

          {/* 결제수단별 상세 입력 (기존 결제폼 재사용, 2단 톤) */}
          {paymentMethod === '카드' && <CardPaymentForm control={control} />}
          {paymentMethod === '현금' && <CashPaymentForm control={control} setValue={setValue} />}
          {paymentMethod === '계좌이체' && <BankTransferForm control={control} setValue={setValue} />}
        </PaidSection>
      )}

      {/* 메모 */}
      <ModalSectionLabel>{t('메모')}</ModalSectionLabel>
      <FormInputTextArea
        control={control}
        minRows={4}
        style={{
          width: '100%',
          padding: '12px 13px',
          fontSize: '14px',
          border: '1px solid #a6a6a6',
          borderRadius: '8px',
          color: '#141414',
        }}
        placeholder={t('메모를 입력해 주세요.')}
        name="memo"
      />
    </CommonModalLayout>
  );
};

export default TransferModal;
